import type { ActionPathStatus, ConnectionStatus } from 'contexts/ConnectionManager';

/**
 * EN-11307: a buy-in while the action socket is down.
 *
 * Registration and re-entry go out as volatile emits on the action socket
 * (GameService.register / tournamentRebuy), which Socket.IO drops while that
 * socket is disconnected: nothing reaches the engine, so nothing is charged,
 * and no reply ever comes. The buy-in's Confirm checks this first and says
 * the connection is lost instead of sending into the void.
 *
 * 'down' only: a context without the field (an older provider, a test
 * harness that never set it) keeps the old behaviour.
 */
export const isActionPathDown = (status: ActionPathStatus | undefined): boolean =>
  status === 'down';

/**
 * EN-11307, QA on the iOS app: the action path alone notices too late. After
 * the network drops, Socket.IO marks the action socket disconnected only once
 * its ping times out (the engine keeps the defaults, so up to ~45 s), while
 * the update socket's keepalive watchdog has already put the connection into
 * 'reconnecting' and the banner up. A buy-in in that window went out on a dead
 * socket and hung. So it also stands down while the connection is
 * reconnecting, or the device says it is offline.
 *
 * 'reconnecting' only, not 'disconnected': that is the state with no socket at
 * all (signed out, or no update socket configured), which disconnectSockets
 * already reports as the action path 'down'.
 */
export const isBuyInConnectionLost = (
  actionPathStatus: ActionPathStatus | undefined,
  connectionStatus: ConnectionStatus | undefined
): boolean =>
  isActionPathDown(actionPathStatus) ||
  connectionStatus === 'reconnecting' ||
  (typeof navigator !== 'undefined' && navigator.onLine === false);

export const CONNECTION_LOST_TITLE = 'Connection lost';

/** What the buy-in says, for the action the player tried ('register' / 're-enter'). */
export const connectionLostMessage = (action: string): string =>
  `You've lost your connection, so we couldn't ${action} you. Nothing was charged. Try again once you're reconnected.`;
